/**
 * Date Format (Same as SimpleDateFormat of Java)
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
import { ComponentElementPropertyType } from "../component_element/ComponentElementManifest.model";

export type DateElementFormat = {
    label: string,
    value: Array<string>
};

export type ElementFormat = {
    label: string,
    value: string
};

export type DateOptionFormat = Array<DateElementFormat>;
export type OptionFormat = Array<ElementFormat>;

export type DateFormatObj = {
    year: string;
    month: string;
    date: string;
    hour: string;
    minute: string;
    second: string;
};

export const dateFormatObj: DateFormatObj = {
    year: "yyyy",
    month: "MM",
    date: "dd",
    hour: "HH",
    minute: "mm",
    second: "ss"
};

export enum DateLabel {
    MONTH_DATE_YEAR = "month_date_year",
    YEAR_MONTH_DATE = "year_month_date",
    DATE_MONTH_YEAR = "date_month_year",
    MONTH_YEAR = "month_year",
    YEAR_MONTH = "year_month",
    MONTH_DATE = "month_date",
    DATE_MONTH = "date_month",
    MONTH = "month",
    HOUR_MINUTE_SECOND = "hour:minute:second",
    HOUR_MINUTE = "hour:minute"
}

export const yearMonthDateFormat: DateOptionFormat = [
    { label: DateLabel.MONTH_DATE_YEAR, value: ["month", "date", "year"]},
    { label: DateLabel.YEAR_MONTH_DATE,  value: ["year", "month", "date"]},
    { label: DateLabel.DATE_MONTH_YEAR, value: ["date", "month", "year"]}
];

export const yearMonthFormat: DateOptionFormat = [
    { label: DateLabel.MONTH_YEAR, value: ["month", "year"]},
    { label: DateLabel.YEAR_MONTH,  value: ["year", "month"]}
];

export const monthDateFormat: DateOptionFormat = [
    { label: DateLabel.MONTH_DATE,  value: ["month", "date"]},
    { label: DateLabel.DATE_MONTH, value: ["date", "month"]}
];

export const monthFormat: DateOptionFormat = [
    { label: DateLabel.MONTH, value: ["month"]}
];

export const timeFormat: DateOptionFormat = [
    { label: DateLabel.HOUR_MINUTE_SECOND,  value: ["hour", "minute", "second"]},
    { label: DateLabel.HOUR_MINUTE, value: ["hour", "minute"]}
];

export const dateSeparator: OptionFormat = [
    { label: "Slash", value: "/" },
    { label: "Dash", value: "-" },
    { label: "Dot", value: "." }
];

export const viewPostfix: OptionFormat = [
    { label: "Slash", value: "/" },
    { label: "Dash", value: "-" },
    { label: "Dot", value: "." },
    { label: "Comma", value: "," },
    { label: "Space", value: " " },
    { label: "Comma and Space", value: ", " }
];

export const viewYearFormat: OptionFormat = [
    {label: "yyyy", value: "yyyy"},
    {label: "yy", value: "yy"}
];

export const viewMonthFormat: OptionFormat = [
    {label: "MMMM", value: "MMMM"},
    {label: "MMM", value: "MMM"},
    {label: "MM", value: "MM"},
    {label: "M", value: "M"}
];

export const viewDateFormat: OptionFormat = [
    {label: "dd", value: "dd"},
    {label: "d", value: "d"}
];

export const viewDayFormat: OptionFormat = [
    {label: "EEEE", value: "EEEE"},
    {label: "EEE", value: "EEE"}
];

export const viewHourFormat: OptionFormat = [
    {label: "HH", value: "HH"},
    {label: "H", value: "H"},
    {label: "hh", value: "hh"},
    {label: "h", value: "h"}
];

export const viewMinuteFormat: OptionFormat = [
    {label: "mm", value: "mm"},
    {label: "m", value: "m"}
];

export const viewSecondFormat: OptionFormat = [
    {label: "ss", value: "ss"},
    {label: "s", value: "s"}
];

export const DateFormatRegexForReplace = /(yyyy|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|H|hh|h|mm|m|ss|s|a)/g;
export const DateFormat = {
    Year: {
        value: {input: { label: "yyyy", value: "yyyy" }, view: { label: "yyyy", value: "yyyy" }},
        options: [
            { label: "yyyy", value: "yyyy" },
            { label: "yy", value: "yy", onlyView: true }
        ],
        suffix: {input: "/", view: "/"},
        [ComponentElementPropertyType.FORMATDATE]: true,
        [ComponentElementPropertyType.FORMATTIME]: false,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: true
    },
    Month: {
        value: {input: { label: "MM", value: "MM" }, view: { label: "MM", value: "MM" }},
        options: [
            { label: "MMMM", value: "MMMM", onlyView: true },
            { label: "MMM", value: "MMM", onlyView: true },
            { label: "MM", value: "MM" },
            { label: "M", value: "M", onlyView: true }
        ],
        suffix: {input: "/", view: "/"},
        [ComponentElementPropertyType.FORMATDATE]: true,
        [ComponentElementPropertyType.FORMATTIME]: false,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: true
    },
    Date: {
        value: {input: { label: "dd", value: "dd" }, view: { label: "dd", value: "dd" }},
        options: [
            { label: "dd", value: "dd" },
            { label: "d", value: "d", onlyView: true }
        ],
        suffix: {input: "/", view: "/"},
        [ComponentElementPropertyType.FORMATDATE]: true,
        [ComponentElementPropertyType.FORMATTIME]: false,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: false
    },
    Day: {
        value: {view: { label: "EEEE", value: "EEEE", onlyView: true }},
        options: [
            { label: "EEEE", value: "EEEE", onlyView: true },
            { label: "EEE", value: "EEE", onlyView: true }
        ],
        onlyView: true,
        suffix: {view: ""},
        [ComponentElementPropertyType.FORMATDATE]: true,
        [ComponentElementPropertyType.FORMATTIME]: false,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: false
    },
    AMPM: {
        value: {view: { label: "a", value: "a", onlyView: true }},
        options: [
            { label: "a", value: "a", onlyView: true }
        ],
        onlyView: true,
        suffix: {view: ""},
        [ComponentElementPropertyType.FORMATDATE]: false,
        [ComponentElementPropertyType.FORMATTIME]: true,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: false
    },
    Hour: {
        value: {input: { label: "HH", value: "HH" }, view: { label: "HH", value: "HH" }},
        options: [
            { label: "HH", value: "HH" },
            { label: "H", value: "H", onlyView: true },
            { label: "hh", value: "hh", onlyView: true },
            { label: "h", value: "h", onlyView: true }
        ],
        suffix: {input: ":", view: ":"},
        [ComponentElementPropertyType.FORMATDATE]: false,
        [ComponentElementPropertyType.FORMATTIME]: true,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: false
    },
    Minute: {
        value: {input: { label: "mm", value: "mm" }, view: { label: "mm", value: "mm" }},
        options: [
            { label: "mm", value: "mm" },
            { label: "m", value: "m", onlyView: true }
        ],
        suffix: {input: ":", view: ":"},
        [ComponentElementPropertyType.FORMATDATE]: false,
        [ComponentElementPropertyType.FORMATTIME]: true,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: false
    },
    Second: {
        value: {input: { label: "ss", value: "ss" }, view: { label: "ss", value: "ss" }},
        options: [
            { label: "ss", value: "ss" },
            { label: "s", value: "s", onlyView: true }
        ],
        suffix: {input: "", view: ""},
        [ComponentElementPropertyType.FORMATDATE]: false,
        [ComponentElementPropertyType.FORMATTIME]: true,
        [ComponentElementPropertyType.FORMATDATETIME]: true,
        [ComponentElementPropertyType.FORMATYEARMONTH]: false
    }
};